import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { formatAuthorMetaLine } from "../utils/displayName";
import AuthorNameLink from "./shared/AuthorNameLink";
import QuestionCard from "./shared/QuestionCard";
import QuestionForm from "./QuestionForm";

const API_URL = getApiBaseUrl();

/**
 * Learning Discussions / Q&A — ask questions, exchange answers, discuss topics.
 */
export default function LearningQABoard({ user, onRefresh }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState("");

  const loadQuestions = useCallback(async () => {
    if (!API_URL) {
      setQuestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        post_type: "question",
        limit: "40",
        offset: "0",
      });
      if (user?.id) params.set("viewer_id", String(user.id));
      const res = await fetch(`${API_URL}/get_posts.php?${params.toString()}`);
      const data = await res.json();
      setQuestions(data.success && Array.isArray(data.posts) ? data.posts : []);
    } catch (err) {
      console.error(err);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const linkPrefix = user ? "/home/question" : "/home/public";
  const q = query.trim().toLowerCase();
  const filtered = !q
    ? questions
    : questions.filter((post) => {
        const hay = [post.title, post.content, post.text, post.category, post.username, post.first_name, post.last_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });

  return (
    <div
      style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: "28px 16px 64px",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: "20px 20px 16px",
          marginBottom: 16,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "#b45309",
            background: "#fef3c7",
            padding: "3px 8px",
            borderRadius: 4,
            marginBottom: 8,
          }}
        >
          Learning Discussions
        </div>
        <h1 style={{ margin: "0 0 8px", fontSize: 28, color: "#0f172a" }}>
          Q&A
        </h1>
        <p style={{ margin: "0 0 16px", color: "#475569", lineHeight: 1.55, fontSize: 15 }}>
          Ask questions, exchange answers, and discuss learning topics with the COOL community.
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
          }}
        >
          {user ? (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              style={{
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "10px 18px",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Ask a question
            </button>
          ) : (
            <Link
              to="/login"
              style={{
                display: "inline-block",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "#fff",
                textDecoration: "none",
                borderRadius: 999,
                padding: "10px 18px",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Log in to ask a question
            </Link>
          )}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            aria-label="Search questions"
            style={{
              flex: "1 1 180px",
              minWidth: 160,
              padding: "9px 12px",
              borderRadius: 999,
              border: "1px solid #e2e8f0",
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>
      </div>

      {loading && <p style={{ color: "#64748b" }}>Loading questions…</p>}

      {!loading && filtered.length === 0 && (
        <p
          style={{
            background: "#fff",
            border: "1px dashed #fcd34d",
            borderRadius: 16,
            padding: 28,
            textAlign: "center",
            color: "#475569",
          }}
        >
          {query.trim()
            ? "No questions match your search."
            : "No questions yet. Be the first to ask something about learning."}
        </p>
      )}

      {!loading &&
        filtered.map((post) => (
          <div
            key={post.id}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <img
                src={resolveAvatarUrl(post, API_URL)}
                alt=""
                onError={handleAvatarError}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <div>
                <AuthorNameLink user={post} style={{ fontWeight: 700, color: "#0f172a" }} />
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {formatAuthorMetaLine(post, (value) =>
                    value ? new Date(value).toLocaleString() : ""
                  )}
                </div>
              </div>
            </div>
            <QuestionCard post={post} linkPrefix={linkPrefix} />
          </div>
        ))}

      {showForm && (
        <QuestionForm
          user={user}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            loadQuestions();
            onRefresh?.();
          }}
          onRefresh={() => {
            loadQuestions();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
